import { createSlice } from '@reduxjs/toolkit';
import { rangeKey } from '../periods';
import { activateAiLicence } from './thunks/activateAiLicence.thunk';
import { cancelAiLicence } from './thunks/cancelAiLicence.thunk';
import { loadAdminAiLicences } from './thunks/loadAdminAiLicences.thunk';
import { loadAdminAiUsage } from './thunks/loadAdminAiUsage.thunk';
import { loadAdminAiUsageMonths } from './thunks/loadAdminAiUsageMonths.thunk';
import { loadAdminEmployerProjects } from './thunks/loadAdminEmployerProjects.thunk';

/**
 * One report per day range, keyed 'from..to', kept so switching back to an
 * already-viewed range is instant. A refresh replaces the range's report; an
 * error is per range too, so a failed range does not hide the ones that
 * loaded.
 *
 * `months` is the list of months that have any usage, which the header's
 * period selects are built from. It is null until loaded; a failed load
 * leaves it null with `monthsError` set, and the page falls back to the
 * current month.
 *
 * `licences` is the licence response: every licence row plus this month's
 * charged businesses, null until loaded. Activating or cancelling one sets
 * `savingBusinessId` until the save and the reload after it are done; a
 * failed save leaves `saveError` set. `employerProjects` feeds the company
 * picker and is loaded only when the add dialog first opens.
 */
const adminAiSlice = createSlice({
  name: 'adminAi',
  initialState: {
    reportsByRange: {},
    loadingRange: null,
    errorByRange: {},
    months: null,
    monthsLoading: false,
    monthsError: null,
    licences: null,
    licencesLoading: false,
    licencesError: null,
    savingBusinessId: null,
    saveError: null,
    employerProjects: null,
    employerProjectsLoading: false,
  },
  reducers: {},
  extraReducers(builder) {
    const errorOf = (action) =>
      action.payload || (action.error && action.error.message) || 'error';

    builder
      .addCase(loadAdminAiUsage.pending, (state, action) => {
        const key = rangeKey(action.meta.arg);
        state.loadingRange = key;
        delete state.errorByRange[key];
      })
      .addCase(loadAdminAiUsage.fulfilled, (state, action) => {
        const key = rangeKey(action.meta.arg);
        state.reportsByRange[key] = action.payload;
        if (state.loadingRange === key) {
          state.loadingRange = null;
        }
      })
      .addCase(loadAdminAiUsage.rejected, (state, action) => {
        const key = rangeKey(action.meta.arg);
        state.errorByRange[key] = errorOf(action);
        if (state.loadingRange === key) {
          state.loadingRange = null;
        }
      })
      .addCase(loadAdminAiUsageMonths.pending, (state) => {
        state.monthsLoading = true;
        state.monthsError = null;
      })
      .addCase(loadAdminAiUsageMonths.fulfilled, (state, action) => {
        state.months = action.payload;
        state.monthsLoading = false;
      })
      .addCase(loadAdminAiUsageMonths.rejected, (state, action) => {
        state.monthsError = errorOf(action);
        state.monthsLoading = false;
      })
      .addCase(loadAdminAiLicences.pending, (state) => {
        state.licencesLoading = true;
        state.licencesError = null;
      })
      .addCase(loadAdminAiLicences.fulfilled, (state, action) => {
        state.licences = action.payload;
        state.licencesLoading = false;
      })
      .addCase(loadAdminAiLicences.rejected, (state, action) => {
        state.licencesError = errorOf(action);
        state.licencesLoading = false;
      })
      .addCase(activateAiLicence.pending, (state, action) => {
        state.savingBusinessId = action.meta.arg.businessId;
        state.saveError = null;
      })
      .addCase(activateAiLicence.fulfilled, (state) => {
        state.savingBusinessId = null;
      })
      .addCase(activateAiLicence.rejected, (state, action) => {
        state.savingBusinessId = null;
        state.saveError = errorOf(action);
      })
      .addCase(cancelAiLicence.pending, (state, action) => {
        state.savingBusinessId = action.meta.arg;
        state.saveError = null;
      })
      .addCase(cancelAiLicence.fulfilled, (state) => {
        state.savingBusinessId = null;
      })
      .addCase(cancelAiLicence.rejected, (state, action) => {
        state.savingBusinessId = null;
        state.saveError = errorOf(action);
      })
      .addCase(loadAdminEmployerProjects.pending, (state) => {
        state.employerProjectsLoading = true;
      })
      .addCase(loadAdminEmployerProjects.fulfilled, (state, action) => {
        state.employerProjects = action.payload;
        state.employerProjectsLoading = false;
      })
      .addCase(loadAdminEmployerProjects.rejected, (state) => {
        state.employerProjectsLoading = false;
      });
  },
});

export default adminAiSlice.reducer;
